'use client';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Image } from '@/app/_components/common/Image';
import { NdrCompanyCardSkeleton } from '@/app_components/NDR/NdrCompanyCard';
import { NdrFaq } from '@/app_components/NDR/NdrFaq';
import { NdrFooter } from '@/app_components/NDR/NdrFooter';
import { NdrHeader } from '@/app_components/NDR/NdrHeader';
import { NdrInContentBanner } from '@/app_components/NDR/NdrInContentBanner';
import { NdrStickyBanner } from '@/app_components/NDR/NdrStickyBanner';
import { NdrStickyBarSkeleton } from '@/app_components/NDR/NdrStickyBar';
import { TableOfContents } from '@/app_components/NDR/TableOfContents';
import { ndrData } from '@/app_data/mock/ndrData';
import { DescriptionParser } from '@/app_lib/htmlParser/DescriptionParser';
import { detectTrafficSource, getNdrHomeUrl, getQueryParamsString } from '@/app_lib/utils/ndrUtils';
import { cn } from '@/app_lib/utils/utils';

const NdrStickyBar = dynamic(() => import('@/app_components/NDR/NdrStickyBar').then((mod) => mod.NdrStickyBar), {
  loading: () => <NdrStickyBarSkeleton />,
  ssr: false,
});

const NdrCompanyCard = dynamic(() => import('@/app_components/NDR/NdrCompanyCard').then((mod) => mod.NdrCompanyCard), {
  loading: () => <NdrCompanyCardSkeleton />,
  ssr: false,
});
// Providers / Hooks

interface NdrWrapperContentProps {
  children?: React.ReactNode;
  source?: 'google' | 'bing';
  slug?: string;
}

interface RetreaverCampaign {
  // biome-ignore lint/style/useNamingConvention: required by Retreaver
  auto_replace_numbers: () => void;
}

export const ExtentedReviewsWrapper = ({ children, source, slug }: NdrWrapperContentProps) => {
  const [stickyBarMounted, setStickyBarMounted] = useState(false);
  const [companyCardMounted, setCompanyCardMounted] = useState(false);

  const retreaverCampaign = useRef<RetreaverCampaign | null>(null);
  const retreaverWaiting = useRef<boolean>(false);
  const stickyTriggerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;

  const searchParams = useSearchParams();
  // Get all query parameters as a string
  const allQueryParamsString = getQueryParamsString(searchParams, source);
  const isGoogleSource = detectTrafficSource(searchParams) === 'google' || source === 'google';
  const homeUrl = getNdrHomeUrl(isGoogleSource ? 'google' : 'bing');

  const data = isGoogleSource ? ndrData.landingPages.google : ndrData.landingPages.bing;
  const reviewItem = data.reviews.find((item) => item.companySlug === slug);
  const ndrSlug = 'national-debt-relief';
  const ndrItem = data.reviews.find((item) => item.companySlug === ndrSlug);

  const enabledData = data.reviews.filter((review) => review.ctas.primary.url !== '');
  const orderedReviews = enabledData.sort((a, b) => a.ordinal - b.ordinal);
  const stickyBarData = enabledData.filter((item) => item.sticky === true)[0];

  // Initialize (once) & run Retreaver to replace phone numbers
  const runRetreaver = useCallback(() => {
    // Package campaign config and phone number replacement into a single function
    // Used either in place, or in a deferred fashion if Retreaver is not yet loaded
    const performAction = () => {
      if (retreaverCampaign.current === null) {
        window.Retreaver.configure({
          host: 'api.calltrackapi.com',
          prefix: 'https',
        });

        retreaverCampaign.current = new window.Retreaver.Campaign({
          // biome-ignore lint/style/useNamingConvention: required by Retreaver
          campaign_key: 'c0ff9c04926762b90d3201cb7444c95b',
        }) as RetreaverCampaign;
      }

      retreaverCampaign.current.auto_replace_numbers();
    };

    // If currently waiting, do nothing
    if (retreaverWaiting.current) {
      return;
    }

    // Set an interval if the script hasn't loaded yet; otherwise, perform action immediately
    if (window.Retreaver === undefined) {
      const retreaveInterval = 100;
      retreaverWaiting.current = true;
      const intvl = setInterval(() => {
        // Once loaded, clear flag, clear interval and perform the action
        // Subsequent calls will go through the immediate path
        if (window.Retreaver !== undefined) {
          retreaverWaiting.current = false;
          clearInterval(intvl);
          performAction();
        }
      }, retreaveInterval);
    } else {
      performAction();
    }
  }, []);

  // Always call useEffect, but conditionally run logic inside
  useEffect(() => {
    if (!reviewItem) {
      return;
    }
    if (!(stickyBarMounted && companyCardMounted)) {
      return;
    }
    runRetreaver();
  }, [stickyBarMounted, companyCardMounted, runRetreaver, reviewItem]);

  // Render NDR page or not found
  if (!reviewItem) {
    return <div>Review not found</div>;
  }

  return (
    <div
      className={cn(
        'ndr-page-wrapper flex flex-col layout-gap-default layout-sticky-safe-area',
        isGoogleSource ? 'is-google-source' : 'is-bing-source',
      )}
    >
      <div className='w-full min-h-16 layout-safe-area shadow-[4px_4px_20px_0_rgba(17,17,17,0.05)]'>
        <NdrHeader
          className='py-xs'
          disclaimerText={data.disclosure.text}
          disclaimerTitle={data.disclosure.title}
          homeUrl={homeUrl}
        />
      </div>
      {/* Sticky trigger point for NdrStickyBar */}
      <div
        className='absolute'
        ref={stickyTriggerRef}
      />
      <div className='w-full lg:container-ft lg:mx-auto px-lg'>
        <div className='grid layout-grid-gap md:grid-cols-[2.5fr_1fr]'>
          <div
            className='flex flex-col flex-1 layout-gap-md [&_p]:typography-level-6 [&_p]:leading-snug [&_h2]:typography-level-2 [&_h2]:font-semibold'
            data-cy='ndr-detailed-review-section'
          >
            {reviewItem?.paragraphs?.map((para, index) => (
              <div
                className='flex flex-col gap-xl'
                key={para}
              >
                {index === 0 && (
                  <h1 className='typography-level-2 font-semibold'>{`Fortune's ${reviewItem.companyName} Review`}</h1>
                )}
                <DescriptionParser content={para} />
                {index === 0 && (
                  <NdrCompanyCard
                    card={reviewItem}
                    isHighlighted
                    onMount={() => setCompanyCardMounted(true)}
                    onResize={runRetreaver}
                    queryParams={allQueryParamsString}
                    variant='fullReview'
                  />
                )}
                {index === 2 && reviewItem.fullReviewHero && (
                  <Image
                    alt={reviewItem.companyName}
                    className='mx-auto'
                    data-cy='full-review-hero-image'
                    fullWidth
                    priority
                    src={reviewItem.fullReviewHero || ''}
                    width={776}
                  />
                )}
              </div>
            ))}
            <NdrInContentBanner
              queryParams={allQueryParamsString}
              review={reviewItem}
            />
          </div>
          {/* Sticky Table of Contents */}
          <div className='md:sticky md:top-4 md:self-start'>
            <div className='flex flex-col layout-nav-gap'>
              <TableOfContents
                queryParams={allQueryParamsString}
                reviews={orderedReviews}
                source={isGoogleSource ? 'google' : 'bing'}
              />
              {ndrItem && (
                <NdrStickyBanner
                  queryParams={allQueryParamsString}
                  review={ndrItem}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <NdrFaq
        className='px-lg'
        faqs={data.faqs}
        title='FAQ'
      />
      <NdrStickyBar
        onMount={() => setStickyBarMounted(true)}
        queryParams={allQueryParamsString}
        review={stickyBarData}
        stickyButtonPhone={data.defaultPhoneNumber}
        triggerElement={stickyTriggerRef}
      />
      <NdrFooter footerContent={data.footer} />
      {children}
    </div>
  );
};
